import { useReactFlow, Node } from '@xyflow/react'
import { useCallback } from 'react'
import { GroupNodeData } from '@/components/blueprints/GroupNode'

export interface UseNodeGroupingResult {
    createGroup: (selectedNodeIds: string[], groupLabel: string, groupType?: 'sequential' | 'parallel' | 'conditional') => string
    createBlueprintContainer: (blueprint: any, position?: { x: number; y: number }) => string
    addNodesToGroup: (groupId: string, nodeIds: string[]) => void
    removeNodesFromGroup: (groupId: string, nodeIds: string[]) => void
    expandGroup: (groupId: string) => void
    collapseGroup: (groupId: string) => void
    deleteGroup: (groupId: string, keepChildren?: boolean) => void
    getGroupNodes: () => Node<GroupNodeData>[]
    getChildrenOfGroup: (groupId: string) => Node[]
}

export const useNodeGrouping = (): UseNodeGroupingResult => {
    const { getNodes, setNodes, getEdges, setEdges } = useReactFlow()

    const createGroup = useCallback((
        selectedNodeIds: string[],
        groupLabel: string,
        groupType: 'sequential' | 'parallel' | 'conditional' = 'sequential'
    ): string => {
        const nodes = getNodes()
        const selectedNodes = nodes.filter(node => selectedNodeIds.includes(node.id))

        if (selectedNodes.length === 0) {
            throw new Error('No nodes selected for grouping')
        }

        // Calculate group position based on selected nodes
        const minX = Math.min(...selectedNodes.map(node => node.position.x))
        const minY = Math.min(...selectedNodes.map(node => node.position.y))
        const maxX = Math.max(...selectedNodes.map(node => node.position.x + (node.width || 200)))
        const maxY = Math.max(...selectedNodes.map(node => node.position.y + (node.height || 100)))

        const groupId = `group_${Date.now()}`
        const groupPosition = {
            x: minX - 20,
            y: minY - 40
        }

        const groupNode: Node<GroupNodeData> = {
            id: groupId,
            type: 'groupnode',
            position: groupPosition,
            data: {
                label: groupLabel,
                description: `Group containing ${selectedNodes.length} nodes`,
                isExpanded: true,
                childNodes: selectedNodeIds,
                groupType,
                backgroundColor: '#f8fafc',
                borderColor: '#3b82f6',
                informationClassification: 'Internal',
                createdBy: 'User',
                createdAt: new Date().toISOString()
            },
            style: {
                width: maxX - minX + 40,
                height: maxY - minY + 80,
                backgroundColor: 'rgba(248, 250, 252, 0.8)',
                border: '2px solid #3b82f6',
                borderRadius: '8px',
                zIndex: -1
            }
        }

        // Add the group node to the canvas
        setNodes(prevNodes => [...prevNodes, groupNode])

        return groupId
    }, [getNodes, setNodes])

    const createBlueprintContainer = useCallback((
        blueprint: any,
        position: { x: number; y: number } = { x: 100, y: 100 }
    ): string => {
        // Create nodes from blueprint definition
        const blueprintNodes: Node[] = []
        const blueprintNodeIds: string[] = []

        if (blueprint.nodeDefinitions && Array.isArray(blueprint.nodeDefinitions)) {
            blueprint.nodeDefinitions.forEach((nodeDef: any, index: number) => {
                const nodeId = `${blueprint.id}_node_${index}_${Date.now()}`
                blueprintNodeIds.push(nodeId)

                const node: Node = {
                    id: nodeId,
                    type: nodeDef.type || 'nodedocument', // fallback to document node
                    position: {
                        x: position.x + 50 + (index % 3) * 220, // arrange in grid inside container
                        y: position.y + 80 + Math.floor(index / 3) * 150
                    },
                    data: {
                        ...nodeDef.data,
                        label: nodeDef.label || `Node ${index + 1}`,
                        // Ensure node has required properties
                        informationClassification: nodeDef.data?.informationClassification || 'Internal',
                        createdBy: 'Blueprint',
                        createdAt: new Date().toISOString()
                    }
                }
                blueprintNodes.push(node)
            })
        }

        // Calculate container dimensions based on child nodes
        const containerWidth = Math.max(400, blueprintNodes.length > 0 ?
            (Math.max(...blueprintNodes.map(n => n.position.x - position.x)) + 220) : 400)
        const containerHeight = Math.max(200, blueprintNodes.length > 0 ?
            (Math.max(...blueprintNodes.map(n => n.position.y - position.y)) + 150) : 200)

        // Create the container group node
        const containerId = `blueprint_${blueprint.id}_${Date.now()}`
        const containerNode: Node<GroupNodeData> = {
            id: containerId,
            type: 'groupnode',
            position,
            data: {
                label: blueprint.name || 'Blueprint Container',
                description: blueprint.description || 'Blueprint imported as container',
                isExpanded: true,
                childNodes: blueprintNodeIds,
                groupType: 'sequential', // blueprints are typically sequential
                backgroundColor: '#fef3c7', // amber-100 for blueprint containers
                borderColor: '#f59e0b', // amber-500
                informationClassification: 'Internal',
                createdBy: 'Blueprint Import',
                createdAt: new Date().toISOString(),
                // Additional blueprint metadata
                blueprintId: blueprint.id,
                blueprintVersion: blueprint.version,
                // Define inputs/outputs for the container
                inputs: blueprint.inputs || [],
                outputs: blueprint.outputs || []
            },
            style: {
                width: containerWidth,
                height: containerHeight,
                backgroundColor: 'rgba(254, 243, 199, 0.8)', // amber-100 with transparency
                border: '2px solid #f59e0b', // amber-500
                borderRadius: '8px',
                zIndex: -1
            }
        }

        // Add all nodes (container + blueprint nodes) to the canvas
        setNodes(prevNodes => [...prevNodes, containerNode, ...blueprintNodes])

        return containerId
    }, [setNodes])

    const addNodesToGroup = useCallback((groupId: string, nodeIds: string[]) => {
        const nodes = getNodes()
        const updatedNodes = nodes.map(node => {
            if (node.id === groupId && node.type === 'groupnode') {
                const currentChildNodes = (node.data as GroupNodeData).childNodes || []
                const newChildNodes = [...new Set([...currentChildNodes, ...nodeIds])]
                return {
                    ...node,
                    data: {
                        ...node.data,
                        childNodes: newChildNodes
                    }
                }
            }
            return node
        })
        setNodes(updatedNodes)
    }, [getNodes, setNodes])

    const removeNodesFromGroup = useCallback((groupId: string, nodeIds: string[]) => {
        const nodes = getNodes()
        const updatedNodes = nodes.map(node => {
            if (node.id === groupId && node.type === 'groupnode') {
                const currentChildNodes = (node.data as GroupNodeData).childNodes || []
                const newChildNodes = currentChildNodes.filter(id => !nodeIds.includes(id))
                return {
                    ...node,
                    data: {
                        ...node.data,
                        childNodes: newChildNodes
                    }
                }
            }
            return node
        })
        setNodes(updatedNodes)
    }, [getNodes, setNodes])

    const expandGroup = useCallback((groupId: string) => {
        const nodes = getNodes()
        const edges = getEdges()

        const groupNode = nodes.find(node => node.id === groupId && node.type === 'groupnode')
        if (!groupNode) return

        const childNodeIds = (groupNode.data as GroupNodeData).childNodes || []

        // Show all child nodes and their edges
        const updatedNodes = nodes.map(node => {
            if (node.id === groupId) {
                return {
                    ...node,
                    data: { ...node.data, isExpanded: true }
                }
            }
            if (childNodeIds.includes(node.id)) {
                return { ...node, hidden: false }
            }
            return node
        })

        const updatedEdges = edges.map(edge => {
            const isChildEdge = childNodeIds.includes(edge.source) || childNodeIds.includes(edge.target)
            if (isChildEdge) {
                return { ...edge, hidden: false }
            }
            return edge
        })

        setNodes(updatedNodes)
        setEdges(updatedEdges)
    }, [getNodes, getEdges, setNodes, setEdges])

    const collapseGroup = useCallback((groupId: string) => {
        const nodes = getNodes()
        const edges = getEdges()

        const groupNode = nodes.find(node => node.id === groupId && node.type === 'groupnode')
        if (!groupNode) return

        const childNodeIds = (groupNode.data as GroupNodeData).childNodes || []

        // Hide all child nodes and their edges
        const updatedNodes = nodes.map(node => {
            if (node.id === groupId) {
                return {
                    ...node,
                    data: { ...node.data, isExpanded: false }
                }
            }
            if (childNodeIds.includes(node.id)) {
                return { ...node, hidden: true }
            }
            return node
        })

        const updatedEdges = edges.map(edge => {
            const isChildEdge = childNodeIds.includes(edge.source) || childNodeIds.includes(edge.target)
            if (isChildEdge) {
                return { ...edge, hidden: true }
            }
            return edge
        })

        setNodes(updatedNodes)
        setEdges(updatedEdges)
    }, [getNodes, getEdges, setNodes, setEdges])

    const deleteGroup = useCallback((groupId: string, keepChildren = true) => {
        const nodes = getNodes()
        const edges = getEdges()

        const groupNode = nodes.find(node => node.id === groupId && node.type === 'groupnode')
        if (!groupNode) return

        const childNodeIds = (groupNode.data as GroupNodeData).childNodes || []

        if (keepChildren) {
            // Remove the group node but keep child nodes visible
            const updatedNodes = nodes
                .filter(node => node.id !== groupId)
                .map(node => {
                    if (childNodeIds.includes(node.id)) {
                        return { ...node, hidden: false }
                    }
                    return node
                })

            const updatedEdges = edges.map(edge => {
                const isChildEdge = childNodeIds.includes(edge.source) || childNodeIds.includes(edge.target)
                if (isChildEdge) {
                    return { ...edge, hidden: false }
                }
                return edge
            })

            setNodes(updatedNodes)
            setEdges(updatedEdges)
        } else {
            // Remove both group and child nodes
            const nodesToRemove = [groupId, ...childNodeIds]
            const updatedNodes = nodes.filter(node => !nodesToRemove.includes(node.id))
            const updatedEdges = edges.filter(edge =>
                !nodesToRemove.includes(edge.source) && !nodesToRemove.includes(edge.target)
            )

            setNodes(updatedNodes)
            setEdges(updatedEdges)
        }
    }, [getNodes, getEdges, setNodes, setEdges])

    const getGroupNodes = useCallback((): Node<GroupNodeData>[] => {
        const nodes = getNodes()
        return nodes.filter(node => node.type === 'groupnode') as Node<GroupNodeData>[]
    }, [getNodes])

    const getChildrenOfGroup = useCallback((groupId: string): Node[] => {
        const nodes = getNodes()
        const groupNode = nodes.find(node => node.id === groupId && node.type === 'groupnode')
        if (!groupNode) return []

        const childNodeIds = (groupNode.data as GroupNodeData).childNodes || []
        return nodes.filter(node => childNodeIds.includes(node.id))
    }, [getNodes])

    return {
        createGroup,
        createBlueprintContainer,
        addNodesToGroup,
        removeNodesFromGroup,
        expandGroup,
        collapseGroup,
        deleteGroup,
        getGroupNodes,
        getChildrenOfGroup
    }
}
